import { Channel, Presence } from 'phoenix'
import { useContext, useEffect, useRef, useState } from 'react'
import { PresenceSocketContext } from './PresenceSocketProvider'

export type PresenceUser = {
  id: string
  name?: string
  avatar_url?: string
  idle?: boolean
}

/**
 * Joins a Phoenix Presence topic and returns the users currently tracked on it.
 *
 * @param topic - The presence topic to join (IE: `presence:/some/route`). Pass an
 *   empty string to skip joining (IE: the feature flag is disabled).
 * @param meta - Display metadata sent at join (IE: `{ name, avatar_url }`). Memoize
 *   it at the call site so a new reference re-joins the channel.
 * @param idle - Whether this user is currently idle. Changes are pushed onto the live
 *   channel (not carried in join meta) so toggling never re-joins the room.
 */
export const usePresence = (
  topic: string,
  meta: Record<string, unknown>,
  idle = false
): PresenceUser[] => {
  const { socket } = useContext(PresenceSocketContext)
  const [users, setUsers] = useState<PresenceUser[]>([])
  const channelRef = useRef<Channel | null>(null)

  // Join the topic and set up presence tracking.
  useEffect(() => {
    if (socket === null || !topic) return

    const channel = socket.channel(topic, meta)
    channelRef.current = channel
    const presence = new Presence(channel)

    // Attach the listener before join(): the initial presence_state fires once, right
    // after join, and would otherwise be missed.
    presence.onSync(() => {
      setUsers(
        presence.list((id, { metas }) => ({
          id,
          ...(metas[0] as Omit<PresenceUser, 'id'>),
        }))
      )
    })

    channel.join()

    return () => {
      channel.leave()
      channelRef.current = null
      setUsers([])
    }
  }, [socket, topic, meta])

  // Propagate idle changes to the already-joined channel. Phoenix buffers the push until
  // the join completes, so an early toggle isn't lost.
  useEffect(() => {
    channelRef.current?.push('update_meta', { idle })
  }, [socket, topic, meta, idle])

  return users
}
